import type { ExecutionDashboardProjection } from '../../routes/execution/dashboardProjection';

export type AttentionFlag =
  | 'active_incident'
  | 'blocking_incident'
  | 'finance_blocked'
  | 'overdue_stop'
  | 'partial_delivery'
  | 'execution_failure'
  | 'incomplete_unload';

export type WorkspaceAttentionProjection = {
  flags: AttentionFlag[];
  highestPriority: AttentionFlag | null;
  overdueStopUids: string[];
};

export type EtaStopFoundation = {
  executionStopUid: string;
  label: string;
  plannedAt: string | null;
  /** Placeholder until real ETA engine — mirrors plannedAt when set. */
  estimatedArrival: string | null;
  isOverdue: boolean;
  deadlineMiss: boolean;
};

export type EtaFoundationProjection = {
  routeEtaLabel: string | null;
  nextStopUid: string | null;
  stops: EtaStopFoundation[];
};

export type WorkspaceComposition = {
  attention: WorkspaceAttentionProjection;
  eta: EtaFoundationProjection;
};

function isPastPlanned(plannedAt: string | null): boolean {
  if (!plannedAt) return false;
  const t = new Date(plannedAt).getTime();
  return Number.isFinite(t) && t < Date.now();
}

function formatEtaShort(plannedAt: string | null): string | null {
  if (!plannedAt) return null;
  try {
    return new Date(plannedAt).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return null;
  }
}

export function buildWorkspaceComposition(
  dashboard: ExecutionDashboardProjection
): WorkspaceComposition {
  const flags: AttentionFlag[] = [];
  const overdueStopUids: string[] = [];

  if (dashboard.incidents.activeCount > 0) flags.push('active_incident');
  if (dashboard.incidents.hasActiveBlocking) flags.push('blocking_incident');
  if (dashboard.financeReadiness.blocked) flags.push('finance_blocked');
  if (dashboard.routeStatus.hasExecutionFailure) flags.push('execution_failure');
  if ((dashboard.progress?.partialCargoCount ?? 0) > 0) flags.push('partial_delivery');

  const incompleteUnload = dashboard.vehicleCargo.items.some(
    (i) => i.currentState === 'partially_unloaded'
  );
  if (incompleteUnload) flags.push('incomplete_unload');

  for (const stop of dashboard.routeFlow.stops) {
    if (!stop.isCompleted && isPastPlanned(stop.plannedAt)) {
      flags.push('overdue_stop');
      overdueStopUids.push(stop.executionStopUid);
    }
  }

  const uniqueFlags = Array.from(new Set(flags));
  const priority: AttentionFlag[] = [
    'blocking_incident',
    'execution_failure',
    'finance_blocked',
    'overdue_stop',
    'partial_delivery',
    'incomplete_unload',
    'active_incident'
  ];
  const highestPriority = priority.find((p) => uniqueFlags.includes(p)) ?? null;

  const etaStops: EtaStopFoundation[] = dashboard.routeFlow.stops.map((stop) => {
    const overdue = !stop.isCompleted && isPastPlanned(stop.plannedAt);
    return {
      executionStopUid: stop.executionStopUid,
      label: stop.label,
      plannedAt: stop.plannedAt,
      estimatedArrival: stop.plannedAt,
      isOverdue: overdue,
      deadlineMiss: overdue && stop.isActive
    };
  });

  const nextStop =
    etaStops.find((s) => {
      const rf = dashboard.routeFlow.stops.find((r) => r.executionStopUid === s.executionStopUid);
      return rf && !rf.isCompleted;
    }) ?? null;

  const routeEtaLabel = nextStop?.estimatedArrival
    ? formatEtaShort(nextStop.estimatedArrival)
    : null;

  return {
    attention: {
      flags: uniqueFlags,
      highestPriority,
      overdueStopUids: Array.from(new Set(overdueStopUids))
    },
    eta: {
      routeEtaLabel,
      nextStopUid: nextStop?.executionStopUid ?? null,
      stops: etaStops
    }
  };
}

export function attentionFlagLabel(flag: AttentionFlag): string {
  const labels: Record<AttentionFlag, string> = {
    active_incident: 'Инцидент',
    blocking_incident: 'Блокировка',
    finance_blocked: 'Оплата',
    overdue_stop: 'Просрочка',
    partial_delivery: 'Частично',
    execution_failure: 'Сбой',
    incomplete_unload: 'Недовыгруз'
  };
  return labels[flag];
}
